import {
  COMMAND_CENTER_BASE_URL,
  COUNTRY_CODES,
  SUPPORT_ACTIVITIES,
  TABLES_KEYS
} from 'features/command-center/constants';
import strings from 'strings';
import { FilterType } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { hasFeatureFlag } from 'UI/utils';
import { OptionRenderers } from 'UI/utils/renderers';

export const PAGE_KEY = TABLES_KEYS.overallPerformance;

const COPIES = strings.commandCenter.overallPerformance;

export const DRAWER_CONTENT_TYPES = {
  NotesForm: 'notesForm',
  FeedbackLog: 'feedbackLog'
};

export const FiltersGroups = [
  {
    id: 'recruiters',
    name: 'Recruiters',
    filters: [
      'employeeNumbers',
      'teams',
      'recruiterStatusesById',
      'healthStatusesById',
      'tenures',
      'channelPartnersEmpNumbers',
      'hireDate',
      'directors',
      'recruitedBy',
      'lastFeedbackActivityTypes',
      'lastFeedbackActivityDate',
      'countryCode'
    ]
  },
  {
    id: 'productivity',
    name: 'Productivity',
    filters: [
      'rangeBySendouts',
      'rangeBySendovers',
      'rangeByPlacements',
      'rangeBySignedAgreements',
      'rangeByActiveSendouts'
    ]
  },
  {
    id: 'financial',
    name: 'Financial',
    filters: [
      'rangeByAvgFeePercentage',
      'rangeByAvgFee',
      'rangeByEstimates',
      'rangeByBillings',
      'rangeByProduction',
      'rangeByCashin',
      'rangeByYearlyProduction',
      'rangeByYearlyCashin',
      'rangeByCareerCashin',
      'rangeByTrackingProduction',
      'rangeByTrackingCashIn',
      'rangeByOutstandingBillings',
      'rangeByDrawBalance'
    ]
  },
  {
    id: 'metrics',
    name: 'Metrics',
    filters: [
      'rangeByInMails',
      'rangeByBulks',
      'rangeByPeopleReached',
      'rangeByPosts',
      'rangeByNewLivePosts',
      'rangeByPhoneTime',
      'rangeByTotalCalls'
    ]
  },
  {
    id: 'inventory',
    name: 'Inventory',
    filters: [
      'rangeByNotSignedCompaniesJobOrders',
      'rangeBySignedCompaniesJobOrders',
      'rangeByCandidates',
      'rangeByContacts',
      'rangeByHiringAuthorities',
      'rangeByCompanies'
    ]
  }
];

export const ORDER_BY_OPTIONS = {
  column: 'recruiterName',
  direction: 'asc'
};

export const METRICS_DESCRIPTION = [
  COPIES.footer.metrics.calls,
  COPIES.footer.metrics.phoneTime,
  COPIES.footer.metrics.bulks,
  COPIES.footer.metrics.peopleReached,
  COPIES.footer.metrics.inMails,
  COPIES.footer.metrics.posts
];

export const extraFilters = [
  {
    name: 'recruiterStatusesById',
    paramName: 'employeeStatuses',
    title: 'Recruiter Status',
    type: FilterType.Autocomplete,
    displayKey: 'title',
    url: `${Endpoints.UserStatuses}?excludeFPstatuses=true`,
    disableListWrap: true,
    multiple: true,
    idKey: 'id'
  },
  {
    name: 'tenures',
    paramName: 'tenures',
    title: 'Tenure',
    type: FilterType.Autocomplete,
    displayKey: 'title',
    api: 'secondary',
    url: `${COMMAND_CENTER_BASE_URL}/catalog/tenures`,
    disableListWrap: true,
    multiple: true,
    idKey: 'id'
  },
  {
    name: 'hireDate',
    type: 'date',
    paramName: 'hireDate',
    title: 'Start Date',
    disableTimeRange: true
  },
  {
    name: 'healthStatusesById',
    paramName: 'statuses',
    title: 'Health Status',
    type: FilterType.Autocomplete,
    displayKey: 'title',
    api: 'secondary',
    url: `${COMMAND_CENTER_BASE_URL}/catalog/health-statuses`,
    disableListWrap: true,
    multiple: true,
    idKey: 'id'
  },
  {
    name: 'rangeBySendouts',
    paramName: 'sendouts',
    title: COPIES.filters.productivity.sendouts,
    type: FilterType.Range,
    displayLabel: COPIES.filters.productivity.sendouts
  },
  {
    name: 'rangeBySendovers',
    paramName: 'sendovers',
    title: COPIES.filters.productivity.sendovers,
    type: FilterType.Range,
    displayLabel: COPIES.filters.productivity.sendovers
  },
  {
    name: 'rangeByPlacements',
    paramName: 'placements',
    title: COPIES.filters.productivity.placements,
    type: FilterType.Range,
    displayLabel: COPIES.filters.productivity.placements
  },
  {
    name: 'rangeBySignedAgreements',
    paramName: 'feeAgreementsSigned',
    title: COPIES.filters.productivity.signedAgreements,
    type: FilterType.Range,
    displayLabel: COPIES.filters.productivity.signedAgreements
  },
  {
    name: 'rangeByEstimates',
    paramName: 'estimates',
    title: COPIES.filters.financial.estimates,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.estimates,
    inputType: 'currency'
  },
  {
    name: 'rangeByBillings',
    paramName: 'billings',
    title: COPIES.filters.financial.billings,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.billings,
    inputType: 'currency'
  },
  {
    name: 'rangeByProduction',
    paramName: 'production',
    title: COPIES.filters.financial.production,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.production,
    inputType: 'currency'
  },
  {
    name: 'rangeByCashin',
    paramName: 'cashin',
    title: COPIES.filters.financial.cashIn,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.cashIn,
    inputType: 'currency'
  },
  {
    name: 'rangeByYearlyProduction',
    paramName: 'productionYTD',
    title: COPIES.filters.financial.yearlyProduction,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.yearlyProduction,
    inputType: 'currency'
  },
  {
    name: 'rangeByYearlyCashin',
    paramName: 'cashinYTD',
    title: COPIES.filters.financial.yearlyCashIn,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.yearlyCashIn,
    inputType: 'currency'
  },
  {
    name: 'rangeByCareerCashin',
    paramName: 'careerCashin',
    title: COPIES.filters.financial.careerCashIn,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.careerCashIn,
    inputType: 'currency'
  },
  {
    name: 'rangeByTrackingProduction',
    paramName: 'trackingProduction',
    title: COPIES.filters.financial.trackingProduction,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.trackingProduction,
    inputType: 'currency'
  },
  {
    name: 'rangeByTrackingCashIn',
    paramName: 'trackingCashIn',
    title: COPIES.filters.financial.trackingCashIn,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.trackingCashIn,
    inputType: 'currency',
    showWhen: () => hasFeatureFlag(FeatureFlags.CommandCenterTrackingCashIn)
  },
  {
    name: 'rangeByOutstandingBillings',
    paramName: 'currentOutstandingBillings',
    title: COPIES.filters.financial.outstandingBillings,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.outstandingBillings,
    inputType: 'currency'
  },
  {
    name: 'rangeByDrawBalance',
    paramName: 'currentDrawBalance',
    title: COPIES.filters.financial.drawBalance,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.drawBalance,
    inputType: 'currency'
  },
  {
    name: 'rangeByAvgFeePercentage',
    paramName: 'avgFeePercentage',
    title: COPIES.filters.financial.avgFeePercentage,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.avgFeePercentage,
    inputType: 'percentage'
  },
  {
    name: 'rangeByAvgFee',
    paramName: 'avgFeeMoney',
    title: COPIES.filters.financial.avgFee,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.avgFee,
    inputType: 'currency'
  },
  {
    name: 'rangeByInMails',
    paramName: 'inMails',
    title: COPIES.filters.metrics.inMails,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.inMails
  },
  {
    name: 'rangeByBulks',
    paramName: 'bulks',
    title: COPIES.filters.metrics.bulks,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.bulks
  },
  {
    name: 'rangeByPeopleReached',
    paramName: 'peopleReached',
    title: COPIES.filters.metrics.peopleReached,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.peopleReached
  },
  {
    name: 'rangeByPosts',
    paramName: 'avgTodayLivePosts',
    title: COPIES.filters.metrics.posts,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.posts
  },
  {
    name: 'rangeByNewLivePosts',
    paramName: 'newLivePosts',
    title: COPIES.filters.metrics.newLivePosts,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.newLivePosts
  },
  {
    name: 'rangeByPhoneTime',
    paramName: 'phoneTime',
    title: COPIES.filters.metrics.phoneTime,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.phoneTime
  },
  {
    name: 'rangeByTotalCalls',
    paramName: 'totalCalls',
    title: COPIES.filters.metrics.totalCalls,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.totalCalls
  },
  {
    name: 'lastFeedbackActivityTypes',
    paramName: 'lastFeedbackActivityTypes',
    title: COPIES.filters.recruiters.lastSupportActivity,
    type: FilterType.Select,
    options: SUPPORT_ACTIVITIES,
    renderOption: OptionRenderers.title
  },
  {
    name: 'lastFeedbackActivityDate',
    paramName: 'lastFeedbackActivityDate',
    title: COPIES.filters.recruiters.lastSupportActivityDate,
    type: 'date',
    disableTimeRange: true
  },
  {
    name: 'recruitedBy',
    paramName: 'recruitedBy',
    title: 'Recruited By',
    type: FilterType.Input
  },
  {
    name: 'channelPartnersEmpNumbers',
    paramName: 'channelPartners',
    title: COPIES.filters.recruiters.channelPartner,
    type: FilterType.Autocomplete,
    displayKey: 'full_name',
    url: `${Endpoints.Users}?extraColumns=external_employee_id`,
    multiple: true,
    idKey: 'external_employee_id'
  },
  {
    name: 'rangeByNotSignedCompaniesJobOrders',
    paramName: 'notSignedCompaniesJo',
    title: COPIES.filters.inventory.notSignedCompaniesJobOrders,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.notSignedCompaniesJobOrders
  },
  {
    name: 'rangeBySignedCompaniesJobOrders',
    paramName: 'signedCompaniesJo',
    title: COPIES.filters.inventory.signedCompaniesJobOrders,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.signedCompaniesJobOrders
  },
  {
    name: 'rangeByCandidates',
    paramName: 'candidatesAdded',
    title: COPIES.filters.inventory.candidates,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.candidates
  },
  {
    name: 'rangeByContacts',
    paramName: 'contactsAdded',
    title: COPIES.filters.inventory.contacts,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.contacts
  },
  {
    name: 'rangeByHiringAuthorities',
    paramName: 'haAdded',
    title: COPIES.filters.inventory.hiringAuthorities,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.hiringAuthorities
  },
  {
    name: 'rangeByCompanies',
    paramName: 'companiesAdded',
    title: COPIES.filters.inventory.companies,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.companies
  },
  {
    name: 'countryCode',
    paramName: 'countryCode',
    title: COPIES.filters.recruiters.country,
    type: FilterType.Select,
    options: COUNTRY_CODES,
    renderOption: OptionRenderers.title
  },
  {
    name: 'rangeByActiveSendouts',
    paramName: 'activeSendouts',
    title: COPIES.filters.productivity.activeSendouts,
    type: FilterType.Range,
    displayLabel: COPIES.filters.productivity.activeSendouts
  }
];

export const TENURE_RANGES = {
  6: '0 - 6',
  12: '7 - 12',
  24: '13 - 24',
  25: '25+'
};
